<template>
  <v-card>
    <div
      ref="mapRef"
      class="map"
      style="height: 500px; width: 100%;"
    >
      <div
        id="loading-message"
        style="text-align: center; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);"
      >
        Preparing map...<br>
      </div>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { useMaps } from '@/composables/useMaps';

/**
 * FormMapField is an input map, where the user clicks a map pin onto a map, and the
 * component outputs the pin coordinates and the nearest available address.
 *
 * IMPORTANT: If we have an address stored, this is the "nearest" address to the pinned
 * location on the map. But the coordinates are what define the location, not the address.
 */

const props = defineProps<{
  zoom: number
}>();

const DEBUG = 0;

const address = defineModel<Address>('address');

const mapRef = ref<HTMLElement | null>(null);
let map: google.maps.Map | null = null;
let marker: google.maps.marker.AdvancedMarkerElement | null = null;
let clickListener: google.maps.MapsEventListener | null = null;

const { googleMapsEmbedApiKey, googleMapsDefaultCenter, googleMapsMapId } = useRuntimeConfig().public;

const maps = await useMaps(googleMapsEmbedApiKey);
const { AdvancedMarkerElement, getCoordinatesFromAddress, getAddressFromCoordinates } = maps;

const waitForMapRef = async () => {
  while (!mapRef.value || mapRef.value.offsetHeight === 0) {
    await new Promise(resolve => requestAnimationFrame(resolve));
  }
};

const getDefaultMapPosition = async () => {
  // Use the composable's geocoder logic
  const pos = await getCoordinatesFromAddress(
    {
      street: googleMapsDefaultCenter,
    } as Address,
    true,
  );
  return pos;
};

const setMapInstance = async (position?: google.maps.LatLng | null) => {
  await waitForMapRef();
  if (DEBUG) {
    console.log('START: setMapInstance');
  }

  if (!mapRef.value) {
    console.error('mapRef is not ready');
    return;
  }

  if (!map) {
    const initZoom = address.value?.coordinates ? props.zoom : 10;

    const center = position || (await getDefaultMapPosition());

    map = new google.maps.Map(mapRef.value, {
      zoom: initZoom,
      zoomControl: true,
      center,
      mapId: googleMapsMapId,
      streetViewControl: false,
      zoomControlOptions: { position: google.maps.ControlPosition.LEFT_BOTTOM },
    });

    if (DEBUG) {
      console.log('Created map on mapRef');
    }
  }
};

const setMarker = async (position: google.maps.LatLng | { lat: number; lng: number }) => {
  if (marker) {
    marker.position = position;
  }
  else {
    // set the marker on the address
    marker = new AdvancedMarkerElement({
      position,
      title: 'Incident address',
      map,
    });
  }
};

// If loading, set the map to the coordinates already defined in the Address.
const setMapCoordinates = async () => {
  await waitForMapRef();
  if (DEBUG) {
    console.log('START: setMapCoordinates');
  }
  if (address.value?.coordinates) {
    const position = await getCoordinatesFromAddress(address.value, false);

    await setMapInstance(position);
    if (!position) {
      console.warn('Could not find valid map coordinates');
      return Promise.resolve();
    }

    setMarker(position);
  }
  else {
    setMapInstance(null);
  }
};

const updateMapWithAddress = async () => {
  if (!address.value || address.value.coordinates === undefined) {
    return;
  }

  if (address.value.coordinates) {
    const position = await getCoordinatesFromAddress(address.value, false);
    if (!position) {
      console.warn('Invalid coordinates, falling back to default');
      await setMapInstance(null);
      return;
    }
    await setMapInstance(position);
    setMarker(position);
  }
  else {
    await setMapInstance(null);
  }
};

const setClickListener = () => {
  if (DEBUG) {
    console.log('START: setClickListener');
    console.log(`!map:${!map}`);
    console.log(`!address:${!address.value}`);
    console.log(`listener:${clickListener}`);
  }
  if (!map || clickListener !== null || !address.value) {
    return;
  }
  clickListener = map.addListener('click', async (e: google.maps.MapMouseEvent) => {
    if (!e.latLng || !address.value) {
      return;
    }
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    const position = new google.maps.LatLng(lat, lng);

    setMarker(position);

    address.value.coordinates = `${lat},${lng}`;

    // Use composable reverse geocoding method
    const newAddress = await getAddressFromCoordinates(lat, lng);
    if (newAddress) {
      address.value.street = newAddress.street;
      address.value.number = newAddress.number;
      address.value.postal_community = newAddress.postal_community;
      address.value.postal_code = newAddress.postal_code;
      address.value.state = newAddress.state;
    }
    else {
      address.value.street = undefined;
      address.value.number = undefined;
      address.value.postal_community = undefined;
      address.value.postal_code = undefined;
      address.value.state = undefined;
    };
  });
  if (DEBUG) {
    console.log('END: setClickListener');
  }
};

// adds and removes listener based on defined_by_coordinates
watch(
  () => address.value?.defined_by_coordinates,
  async newValue => {
    await nextTick();
    if (DEBUG) {
      console.log(`WATCH: coordinate change: ${newValue}`);
    }
    if (newValue) {
      if (!map) {
        await setMapInstance(null);
        await nextTick();
      }
      await updateMapWithAddress();
      setClickListener();
    }
    else if (clickListener) {
      clickListener.remove();
      clickListener = null;
    }
  },
  { immediate: true },
);

onMounted(async () => {
  await nextTick();
  if (DEBUG) {
    console.log('START: onMounted');
  }
  await updateMapWithAddress();
  setClickListener();
});

watch(mapRef, async newMapRef => {
  if (newMapRef && !map) {
    await nextTick();
    await setMapCoordinates();
    setClickListener();
  }
});
</script>

<style scoped>
.map {
  height: calc(100% + var(--v-layout-bottom) - 1rem);
}
</style>
